import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { useLocation, useNavigate } from "react-router-dom";

import { StarIcon } from "@heroicons/react/24/solid";
import { CameraIcon, TruckIcon, UserIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { createColumnHelper, RowModel, SortingState, Table } from "@tanstack/react-table";

import Avatar from "../../user/Avatar";
import Modal from "../../../layouts/Modal";
import useLeads from "../../../data/useLeads";
import classNames from "../../../utils/classNames";
import useMessages from "../../../data/useMessages";
import useSav, { ISavCustomerName } from "../../../data/useSav";
import BulkCompleteLeads from "./BulkCompleteLeads";
import PriceFormat from "../../../utils/priceFormat";
import openInNewTab from "../../../utils/openInNewTab";
import formatDateTime from "../../../utils/formatDateTime";
import { getLeadLocation, getLeadPostcode } from "../../../utils/getLeadLocation";
import { useSignalR } from "../../../contexts/SignalRContext";
import useLeadsDocuments from "../../../data/useLeadsDocuments";
import useNotification from "../../notifications/useNotifications";
import LeadDocumentCell from "./TableSubComponents/LeadDocumentsCell";
import LeadUnreadMessage from "./TableSubComponents/LeadUnreadMessage";
import LeadCheckInStatusCell from "./TableSubComponents/LeadCheckInStatusCell";
import { ILead, LeadQualified, LeadStatus } from "../../../models/Lead";
import { OfferType } from "../../../models/EnginesWorldwide";
import { BookDropOffModal } from "./TableSubComponents/BookDropOffModal";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import LeadsTableCallCount from "./TableSubComponents/LeadsTableCallCount";
import LeadsTableSubComponent from "./TableSubComponents/LeadsTableSubComponent";
import { CollectionReminder, QualifyLead } from "../../../services/TemplateService";
import LeadStatusCell from "../LeadStatusCell";
import PagedTable from "../../shared/Tables/PagedTable";
import TableCheckboxCol from "../../shared/Tables/IndeterminateCheckbox";
import TableVehicleDetails from "../../vehicles/VehiclesTable/TableVehicleDetails";
import TableVehicleContact from "../../vehicles/VehiclesTable/TableVehicleContact";
import Tooltip from "../../shared/Tooltip";
import { useAuth } from "../../../contexts/AuthContext";
import { wilmaSAVCustomerId } from "../../../config";
import getTextColorForBackground from "../../../utils/getTextColorForBackground";


const colHelper = createColumnHelper<ILead>();

const formatCollectionDate = (dateValue?: string | null) => {
  if (!dateValue) return "";
  return formatDateTime(
    dateValue,
    dateValue.includes("T") ? DateTime.DATETIME_SHORT : DateTime.DATE_SHORT
  );
};

const getEwwOfferValue = (lead: ILead, type: OfferType): number => {
  const directOffer = type === OfferType.Engine
    ? lead.vehicle?.enginesWorldwideEngineOffer
    : lead.vehicle?.enginesWorldwideVfeOffer;

  if (directOffer) {
    return parseFloat(directOffer);
  }

  const fromArray = lead.vehicle?.enginesWorldwide?.find(e => e.type === type);
  if (fromArray?.price) {
    return parseFloat(fromArray.price.toString());
  }

  return 0;
};

const getEwwTotalExportValue = (lead: ILead): number =>
  getEwwOfferValue(lead, OfferType.Engine) + getEwwOfferValue(lead, OfferType.Vfe);

const SelectCell = ({ row }: { row: any }) => {
  const isLocal = process.env.NODE_ENV === "development";
  const { activeLeads } = useSignalR();
  const lead = row.original;

  return (
    <div className="flex items-center p-4" onClick={(e) => e.stopPropagation()}>
      {/* Turned this off on local because it was flickering */}
      {/* Render the avatar if the lead is open */}
      {!isLocal && activeLeads && activeLeads[lead.id] && (
        <div className="ml-2 flex">
          {activeLeads[lead.id].map((viewer, key) => (
            <Avatar
              key={key}
              userId={viewer}
              classes="w-8 h-8 rounded-full [&:not(:first-child)]:-ml-2"
            />
          ))}
        </div>
      )}
    </div>
  );
};

const InJobCell = ({ row }: { row: any }) => {
  const { activeJobSessions } = useSignalR();
  const lead = row.original;
  const users = activeJobSessions[lead.id];

  if (!users?.length) return null;

  return (
    <div className="flex items-center gap-1 px-2">
      <WrenchScrewdriverIcon className="w-4 h-4 text-amber-500 shrink-0" />
      <div className="flex">
        {users.map((userId, key) => (
          <Avatar
            key={key}
            userId={userId}
            classes="w-7 h-7 rounded-full [&:not(:first-child)]:-ml-2"
            textClasses="text-[10px]"
          />
        ))}
      </div>
    </div>
  );
};

interface IUnreadMessage {
  leadId: string;
  unreadMessageCount: number;
}

export default function LeadsTable({
  additionalBody,
  showUnread,
  unreadMessages,
  isSavCustomer = false,
  savCustomers = [],
}: {
  additionalBody?: Record<string, any>;
  showUnread?: boolean;
  unreadMessages?: IUnreadMessage[];
  isSavCustomer?: boolean;
  savCustomers?: ISavCustomerName[];
}) {
  const { pathname } = useLocation();
  const { customerId } = useAuth();
  const { bulkUpdate, bulkComplete } = useLeads(true, "LeadsTable");
  const { allocateAtf } = useSav(
    isSavCustomer && customerId === wilmaSAVCustomerId ? customerId : undefined,
    true,
    "LeadsTable"
  );
  const { sendText } = useMessages();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Leads");
  const [updateOpen, setUpdateOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("0");
  const [selectedText, setSelectedText] = useState("qualify");
  const [leads, setLeads] = useState<ILead[]>([]);
  const [table, setTable] = useState<Table<ILead>>();

  const { leadsDocuments } = useLeadsDocuments(leads.map((l) => l.id), "LeadsTable");
  const [documents, setDocuments] = useState<any[]>([]);
  const isSavBookingPath = isSavCustomer && pathname.startsWith("/bookings");
  const savCustomerById = useMemo(
    () => new Map(savCustomers.map((customer) => [customer.id, customer])),
    [savCustomers]
  );

  useEffect(() => {
    if (leadsDocuments.isLoading) return;
    if (!leadsDocuments.data) return;

    setDocuments(leadsDocuments.data.data);
  }, [leadsDocuments.isLoading, leadsDocuments.data]);


  const handleRowUpdate = () => {
    if (!table) return;

    var leadIds = new Array();

    table.getSelectedRowModel().flatRows.forEach((lead) => {
      leadIds.push(lead.original.id);
    });

    const body = {
      status: parseInt(selectedStatus as keyof LeadStatus),
      leads: leadIds,
    };

    bulkUpdate.mutate(body, {
      onSuccess: () => {
        table.resetRowSelection();
        saveHandlers.onSuccess();
        setUpdateOpen(false);
      },
      onError: saveHandlers.onError,
    });
  };

  const handleBulkText = () => {
    if (!table) return;

    let selectedLeads = new Array();

    table.getSelectedRowModel().flatRows.forEach((lead) => {
      selectedLeads.push(lead.original);
    });

    let templateId = "";

    if (selectedText === "qualify") {
      templateId = QualifyLead.Message;
    } else if (selectedText === "collection") {
      templateId = CollectionReminder.Message;
    }

    selectedLeads.forEach((lead) => {
      const body = {
        leadId: lead.id,
        templateId: templateId,
        contactId: lead.contact.id,
        recipient: lead.contact.telephone1,
        substitutions: {
          "{{name}}": lead.contact?.fullname!,
          "{{make}}": lead.vehicle?.combinedMake!,
          "{{model}}": lead.vehicle?.combinedModel!,
          "{{telephone}}": lead.contact.telephone1!,
          "{{price}}": lead.price.toString()!,
        },
      };

      sendText.mutate(body, {
        onSuccess: () => {
          table.resetRowSelection();
          setTextOpen(false);
          addNotification({
            variant: "success",
            primaryText: "Texts sent successfully",
          });
        },
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "Error sending texts",
          });
        },
      });
    });
  };

  const cols = useMemo(() => [
    TableCheckboxCol(true, true),
    colHelper.accessor("vehicle.vrm", {
      header: "Details",
      id: "details",
      sortingFn: "auto",
      cell: (info) => (
        <TableVehicleDetails
          vehicle={info.row.original.vehicle}
          price={info.row.original.price}
          lead={info.row.original}
          showEbaySearch
        />
      ),
    }),
    colHelper.accessor("contactId", {
      header: "Customer",
      id: "details",
      sortingFn: "auto",
      cell: (info) => <div className="flex flex-col gap-y-2">
        <TableVehicleContact contact={info.row.original.contact} lead={info.row.original} />
        <LeadDocumentCell loading={leadsDocuments.isLoading} documents={documents.filter(d => d.leadId === info.row.original.id)} />
      </div>
    }),
    colHelper.accessor("vehicle.postcode", {
      header: "Address",
      enableSorting: false,
      cell: (info) => {
        const lead = info.row.original;
        const location = getLeadLocation(lead);
        const postcode = getLeadPostcode(lead);
        const line1 = location?.line1;

        return <div>
            {!lead.vehicle!.isDropOff
              ? (
                <div>
                  <p>{postcode}</p>
                  <p>
                    {line1 ? (
                      line1
                    ) : (
                      <span className="text-red-600 font-semibold">
                        No Address Line 1 Found
                      </span>
                    )}
                  </p>
                </div>
              )
              : lead.vehicle!.isDropOff && (
                <span className="px-2 py-1 rounded-full font-semibold bg-blue-100 text-blue-500 whitespace-nowrap">
                  Drop Off
                </span>
              )}
          </div>;
      },
    }),
    colHelper.accessor("status", {
      header: "Status",
      enableSorting: false,
      cell: (info) => {
        const allocatedCustomerId = info.row.original.allocatedToCustomerId;
        const allocatedCustomer = allocatedCustomerId ? savCustomerById.get(allocatedCustomerId) : null;
        const allocatedBackground = allocatedCustomer?.primaryColour || "#E0E7FF";
        const allocatedText = getTextColorForBackground(allocatedBackground);

        return (
          <div className="flex flex-col items-center">
            <LeadStatusCell
              status={info.getValue()}
              website={info.row.original.website}
              quoteType={info.row.original.quoteType}
            />
            {isSavBookingPath && (
              allocatedCustomerId ? (
                <span
                  className="mt-3 inline-flex w-fit items-center justify-center rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap text-center"
                  style={{ backgroundColor: allocatedBackground, color: allocatedText }}
                >
                  {allocatedCustomer?.name || "Allocated"}
                </span>
              ) : (
                <span className="mt-2 inline-flex w-fit items-center justify-center rounded-full px-3 py-1 text-xs font-semibold bg-amber-100 text-amber-700 whitespace-nowrap text-center">
                  Unallocated
                </span>
              )
            )}
          </div>
        );
      },
    }),
    colHelper.accessor("callCount", {
      header: "Call List",
      enableSorting: false,
      cell: (info) => <LeadsTableCallCount lead={info.row.original} />,
    }),
    colHelper.accessor("vehicle.scheduledCollectionOn", {
      header: "Collection",
      sortingFn: "auto",
      cell: (info) => {
        const lead = info.row.original;
        const isDropOff = lead.vehicle!.isDropOff;
        const isCollected = !!lead.vehicle?.collectedOn;

        return isDropOff ? (
          <div onClick={(e) => e.stopPropagation()}>
            <BookDropOffModal currentLead={lead} />
          </div>
        ) : (
          <div className="flex flex-col gap-y-1">
            <span
              className={classNames(
                "inline-flex w-fit rounded-full px-2 py-1 text-xs font-semibold whitespace-nowrap",
                isCollected
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600"
              )}
            >
              {isCollected ? "Collected" : "Awaiting Collection"}
            </span>
            <span className="text-sm text-gray-700">
              {isCollected
                ? formatCollectionDate(lead.vehicle?.collectedOn)
                : formatCollectionDate(info.getValue())}
            </span>
          </div>
        );
      },
    }),
    colHelper.accessor("vehicle.checkInId", {
      header: "Check-In",
      id: "checkInStatus",
      enableSorting: false,
      cell: (info) => (
        <LeadCheckInStatusCell
          checkInId={info.getValue()}
        />
      ),
    }),
    colHelper.display({
      id: "inJob",
      header: "In Job",
      cell: ({ row }) => <InJobCell row={row} />,
    }),
    colHelper.accessor("vehicle.enginesWorldwideEngineOffer", {
      header: "Export Value",
      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        //Sort by value of both engine export and front end, falling back to the enginesWorldwide array
        return getEwwTotalExportValue(rowA.original) - getEwwTotalExportValue(rowB.original);
      },
      cell: (info) => (
        <span>
          {PriceFormat(getEwwTotalExportValue(info.row.original))}
        </span>
      ),
    }),
    colHelper.accessor("id", {
      header: "Unread Messages",
      sortingFn: "auto",
      cell: (info) => <LeadUnreadMessage lead={info.row.original} />,
    }),
    colHelper.accessor("created", {
      header: "Created",
      sortingFn: "auto",
      cell: (info) => (formatDateTime(info.getValue(), DateTime.DATETIME_SHORT)),
    }),
  ], [documents, leadsDocuments.isLoading, isSavBookingPath, savCustomerById]);

  const handleRowColor = (lead: ILead) => {
    let classNames = "";

    if (!lead.vehicle?.found) classNames = "bg-gray-200 ";
    else if (lead.vehicle.isDropOff === true) classNames = "bg-orange-100 ";
    else if (
      lead.qualified === LeadQualified.Qualified &&
      lead.status === LeadStatus["Awaiting Contact"]
    )
      classNames = "bg-green-200 ";
    else if (
      lead.qualified === LeadQualified.Stop &&
      lead.status === LeadStatus["Awaiting Contact"]
    )
      classNames = "bg-red-200 ";
    else if (
      lead.qualified === LeadQualified["No Match"] &&
      lead.status === LeadStatus["Awaiting Contact"]
    )
      classNames = "bg-yellow-200 ";

    // if (lead.vehicle?.vehiclePremium) {
    //   classNames += "border-4 border-amber-500 "
    // }

    return classNames;
  };

  const leadBasePath = (pathname.startsWith("/bookings") || pathname.startsWith("/quotes")) ? "bookings" : "leads";

  const handleAllocateLead = (leadId: string, targetCustomerId: string) => {
    if (!targetCustomerId) return;

    allocateAtf.mutate(
      {
        leadId,
        targetCustomerId,
      }, 
      saveHandlers
    );
  };

  return (
    <>
      <Modal width="max-w-2xl" open={updateOpen} setOpen={setUpdateOpen}>
        <h1 className="text-xl">Bulk Update</h1>
        <div className="mt-4">
          <p>Change all selected leads to which status?</p>
          <div>
            <select
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="mt-2 border border-gray-400 rounded-md text-sm"
              name="leadStatus"
            >
              {Object.values(LeadStatus)
                .filter((ct) => isNaN(Number(ct)))
                .map((status) => (
                  <option key={status} value={LeadStatus[status as keyof typeof LeadStatus]}>
                    {status}
                  </option>
                ))}
            </select>
          </div>
          <button
            className="mt-5 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
            onClick={handleRowUpdate}
          >
            Update
          </button>
        </div>
      </Modal>
      <Modal width="max-w-2xl" open={textOpen} setOpen={setTextOpen}>
        <h1 className="text-xl">Bulk Text</h1>
        <div className="mt-4">
          <p>Send which text to all of these leads?</p>
          <div>
            <select
              onChange={(e) => setSelectedText(e.target.value)}
              className="mt-2 border border-gray-400 rounded-md text-sm"
              name="textType"
            >
              <option key={0} value={"qualify"}>
                Qualifying Text
              </option>
              <option key={1} value={"collection"}>
                Collection Reminder
              </option>
            </select>
          </div>
          <button
            className="mt-5 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
            onClick={handleBulkText}
          >
            Send
          </button>
        </div>
      </Modal>
      <BulkCompleteLeads
        table={table}
        open={completeOpen}
        setOpen={setCompleteOpen}
      />

      <PagedTable
        columns={cols}
        url="/lead/partialwithfilters"
        queryLabel="Search Name, VRM or Postcode"
        expandedRow={(lead: ILead) => (
          <LeadsTableSubComponent
            lead={lead}
            isSavCustomer={isSavCustomer}
            savCustomers={savCustomers}
            onAllocateLead={handleAllocateLead}
            allocatingLeadId={allocateAtf.isLoading ? (allocateAtf.variables as any)?.leadId ?? null : null}
          />
        )}
        additionalBody={{
          ...additionalBody
        }}
        onRowClick={(x: ILead) => openInNewTab(`${leadBasePath}/${x.id}`)}
        rowColor={handleRowColor}
        setRowData={setLeads}
        actionButtons={[
          {
            label: "Bulk Update",
            onClick: (rows: RowModel<ILead>, table: Table<ILead>) => {
              setTable(table);
              setUpdateOpen(true);
            },
            disabledOnNoRows: true,
            isLoading: false,
          },
          {
            label: "Bulk Text",
            onClick: (rows: RowModel<ILead>, table: Table<ILead>) => {
              setTable(table);
              setTextOpen(true);
            },
            disabledOnNoRows: true,
            isLoading: false,
          },
          {
            label: "Bulk Actions",
            onClick: (rows: RowModel<ILead>, table: Table<ILead>) => {
              setTable(table);
              setCompleteOpen(true);
            },
            disabledOnNoRows: true,
            isLoading: false,
          },
        ]}
      />
    </>
  );
}
